import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BookOpen, ClipboardCheck, ListTodo, Route, ChevronRight, CheckCircle2, Lock, Clock, Brain } from 'lucide-react';
import { motion } from 'framer-motion';
import LearningPathView from '../components/LearningPathView';

const fadeUp = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

function TestCard({ item, i, navigate, myProgress }) {
  const results = myProgress?.test_results?.filter(r => r.test_id === item.id) || [];
  const bestScore = results.length > 0 ? Math.max(...results.map(r => r.score)) : null;
  const passed = results.some(r => r.passed);
  const isLocked = item.locked;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
      <Card className={`rounded-2xl border shadow-sm transition-all ${isLocked ? 'border-stone-200 bg-stone-50 opacity-70' : 'bg-white border-stone-200 hover:shadow-md cursor-pointer'}`}
        onClick={() => !isLocked && navigate(`/test/${item.id}`)} data-testid={`test-card-${item.id}`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                {isLocked && <Lock className="w-4 h-4 text-stone-400" />}
                <h3 className={`font-semibold ${isLocked ? 'text-stone-400' : 'text-stone-900'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
              </div>
              <p className={`text-sm mt-1 ${isLocked ? 'text-stone-400' : 'text-stone-500'}`}>{item.questions?.length || 0} questions - Pass: {item.passing_score}%</p>
              {isLocked && <p className="text-xs text-amber-600 mt-2 font-medium">{item.lock_reason}</p>}
              {!isLocked && bestScore !== null && (
                <Badge className={`mt-2 ${passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>Best: {bestScore}% {passed ? '- Passed' : '- Failed'}</Badge>
              )}
            </div>
            {!isLocked && <ChevronRight className="w-5 h-5 text-stone-400" />}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function TaskCard({ item, i, completeTask }) {
  const isLocked = item.locked;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
      <Card className={`rounded-2xl border shadow-sm ${isLocked ? 'border-stone-200 bg-stone-50 opacity-70' : item.completed ? 'border-green-200 bg-green-50/30' : 'border-stone-200 bg-white'}`} data-testid={`task-card-${item.id}`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {isLocked && <Lock className="w-4 h-4 text-stone-400" />}
                <h3 className={`font-semibold ${isLocked ? 'text-stone-400' : item.completed ? 'text-stone-400 line-through' : 'text-stone-900'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <Badge className={item.priority === 'high' ? 'bg-red-100 text-red-700' : item.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'}>{item.priority}</Badge>
              </div>
              <p className={`text-sm mt-1 ${isLocked ? 'text-stone-400' : 'text-stone-500'}`}>{item.description}</p>
              {isLocked && <p className="text-xs text-amber-600 mt-2 font-medium">{item.lock_reason}</p>}
            </div>
            {!isLocked && !item.completed ? (
              <Button onClick={() => completeTask(item.id)} className="bg-[#1B2A4A] text-white rounded-xl text-sm hover:bg-[#243658] shrink-0" data-testid={`complete-task-${item.id}`}>Complete</Button>
            ) : !isLocked && item.completed ? <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" /> : null}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function ExtraModuleCard({ item, i, navigate }) {
  const pct = item.user_progress?.percentage || 0;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
      <Card className="rounded-2xl border border-stone-200 bg-white shadow-sm hover:shadow-md cursor-pointer" onClick={() => navigate(`/training/${item.id}`)} data-testid={`module-card-${item.id}`}>
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-semibold text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h4>
            <p className="text-xs text-stone-500 line-clamp-1">{item.description}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="w-24 h-1.5 bg-stone-100 rounded-full overflow-hidden"><div className="h-full bg-[#1B2A4A]" style={{ width: `${pct}%` }} /></div>
              <span className="text-[11px] text-stone-500">{pct}%</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" />
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function StaffDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [modules, setModules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tests, setTests] = useState([]);
  const [myProgress, setMyProgress] = useState(null);
  const [refreshers, setRefreshers] = useState([]);
  const [activeTab, setActiveTab] = useState('path');

  const loadData = useCallback(async () => {
    try {
      const [pathsRes, modulesRes, tasksRes, testsRes, progressRes, refRes] = await Promise.all([
        api('get', '/learning-paths/my'), api('get', '/training-modules'), api('get', '/tasks'), api('get', '/tests'), api('get', '/progress/my'), api('get', '/refreshers/my')
      ]);
      setPaths(pathsRes.data); setModules(modulesRes.data); setTasks(tasksRes.data); setTests(testsRes.data); setMyProgress(progressRes.data); setRefreshers(refRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load data:', err);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const completeTask = async (taskId) => {
    try { await api('post', `/tasks/${taskId}/complete`); loadData(); }
    catch (err) { if (process.env.NODE_ENV === 'development') console.error('Failed to complete task:', err); }
  };

  const pathModuleIds = new Set(paths.flatMap(p => p.modules.map(m => m.id)));
  const extraModules = modules.filter(m => !pathModuleIds.has(m.id));
  const totalPathModules = paths.reduce((s, p) => s + p.total, 0);
  const approvedPathModules = paths.reduce((s, p) => s + p.approved_count, 0);
  const pendingCount = paths.reduce((s, p) => s + p.modules.filter(m => m.status === 'pending_signoff').length, 0);
  const currentPath = paths.find(p => !p.complete && p.total > 0);
  const currentModule = currentPath?.current_module;
  const pathPct = totalPathModules ? Math.round((approvedPathModules / totalPathModules) * 100) : 0;

  return (
    <div className="pb-20 md:pb-6">
      <motion.div {...fadeUp} className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome back, {user?.name}</h1>
        <p className="text-stone-500 mt-1 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Continue your training journey
          {user?.position && <span className="ml-1">| {user.position}</span>}
          {user?.department && <span className="ml-1">| {user.department}</span>}
        </p>
      </motion.div>

      <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="stat-progress">
          <CardContent className="p-4"><Route className="w-5 h-5 text-[#D9480F] mb-2" /><p className="text-2xl font-bold text-stone-900">{pathPct}%</p><p className="text-xs text-stone-500">Path Progress</p></CardContent>
        </Card>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="stat-modules">
          <CardContent className="p-4"><BookOpen className="w-5 h-5 text-[#D9480F] mb-2" /><p className="text-2xl font-bold text-stone-900">{approvedPathModules}/{totalPathModules}</p><p className="text-xs text-stone-500">Modules Signed Off</p></CardContent>
        </Card>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="stat-pending">
          <CardContent className="p-4"><Clock className="w-5 h-5 text-[#D9480F] mb-2" /><p className="text-2xl font-bold text-stone-900">{pendingCount}</p><p className="text-xs text-stone-500">Awaiting Sign-off</p></CardContent>
        </Card>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="stat-tests">
          <CardContent className="p-4"><ClipboardCheck className="w-5 h-5 text-[#D9480F] mb-2" /><p className="text-2xl font-bold text-stone-900">{myProgress?.avg_test_score || 0}%</p><p className="text-xs text-stone-500">Avg Quiz Score</p></CardContent>
        </Card>
      </motion.div>

      {refreshers.filter(r => r.status === 'due').map(r => (
        <motion.div key={r.id} {...fadeUp} transition={{ delay: 0.12 }}>
          <Card className="rounded-2xl border border-[#D9480F]/40 bg-[#FFF4E6] shadow-sm mb-4" data-testid={`refresher-due-${r.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#D9480F] text-white flex items-center justify-center shrink-0"><Brain className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#D9480F] font-semibold">{r.interval_days}-day refresher</p>
                  <p className="text-sm font-semibold text-stone-900 truncate">{r.module_title}</p>
                  <p className="text-xs text-stone-500">3 quick questions to keep it fresh</p>
                </div>
              </div>
              <Button onClick={() => navigate(`/refresher/${r.id}`)} className="bg-[#D9480F] hover:bg-[#c2410c] text-white rounded-xl shrink-0 text-sm" data-testid={`take-refresher-${r.id}`}>Take it</Button>
            </CardContent>
          </Card>
        </motion.div>
      ))}

      {currentModule && (
        <motion.div {...fadeUp} transition={{ delay: 0.15 }}>
          <Card className="rounded-2xl border-0 bg-[#1B2A4A] text-white shadow-md mb-6 overflow-hidden relative" data-testid="current-module-card">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#D9480F]/20" />
            <CardContent className="p-5 relative z-10 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">{currentPath.name} · Module {currentModule.order + 1} of {currentPath.total}</p>
                <h3 className="text-lg font-semibold truncate mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{currentModule.title}</h3>
                <p className="text-xs text-white/70 mt-1">
                  {currentModule.status === 'pending_signoff' ? 'Quiz passed — waiting for your manager to sign off.' : currentModule.status === 'rejected' ? 'Sent back by your manager — review the note and retake the quiz.' : 'Read the module, then pass the quiz to send it for sign-off.'}
                </p>
              </div>
              <Button onClick={() => navigate(`/training/${currentModule.id}`)} className="bg-[#D9480F] hover:bg-[#c2410c] text-white rounded-xl shrink-0" data-testid="continue-current-module-btn">
                {currentModule.status === 'pending_signoff' ? 'View' : 'Continue'} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-stone-100 rounded-xl p-1 mb-4" data-testid="dashboard-tabs">
          <TabsTrigger value="path" className="flex-1 rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]" data-testid="tab-path">My Path</TabsTrigger>
          <TabsTrigger value="tests" className="flex-1 rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]" data-testid="tab-tests">Quizzes</TabsTrigger>
          <TabsTrigger value="tasks" className="flex-1 rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]" data-testid="tab-tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="path">
          <LearningPathView paths={paths} navigate={navigate} />
          {extraModules.length > 0 && (
            <div className="mt-8" data-testid="extra-modules">
              <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3 px-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Additional Training</h3>
              <div className="space-y-3">{extraModules.map((m, i) => <ExtraModuleCard key={m.id} item={m} i={i} navigate={navigate} />)}</div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="tests">
          <div className="space-y-3" data-testid="tests-list">
            {tests.length === 0 && (
              <Card className="bg-white rounded-2xl border border-stone-200"><CardContent className="p-8 text-center"><ClipboardCheck className="w-12 h-12 text-stone-300 mx-auto mb-3" /><p className="text-stone-500">No quizzes available yet</p></CardContent></Card>
            )}
            {tests.map((t, i) => <TestCard key={t.id} item={t} i={i} navigate={navigate} myProgress={myProgress} />)}
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="space-y-3" data-testid="tasks-list">
            {tasks.length === 0 && (
              <Card className="bg-white rounded-2xl border border-stone-200"><CardContent className="p-8 text-center"><ListTodo className="w-12 h-12 text-stone-300 mx-auto mb-3" /><p className="text-stone-500">No tasks assigned yet</p></CardContent></Card>
            )}
            {tasks.map((t, i) => <TaskCard key={t.id} item={t} i={i} completeTask={completeTask} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
